import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { closedNote, hotNotStarted, sortLeastCompleteFirst, windowMode } from '../benchmarkMonitoring';
import { BENCH } from '../tokens';
import { SHOWN_BENCHMARK_KEYS, type BenchmarkKey, type BenchmarkMonitoring, type MonitoringGroup } from '../types';
import { ProgressBar } from './ProgressBar';
import { CARD_SX, CHEVRON_WIDTH, COUNT_WIDTHS, CardHeading, CountCell, NameCell, ROW_SX, StatusHeaderRow } from './tableParts';

const TAB_LABELS: Record<BenchmarkKey, string> = { speaking: 'Speaking', writing: 'Writing', reading: 'Reading', listening: 'Listening' };

export function BenchmarkSwitcher({ value, onChange }: { value: BenchmarkKey; onChange: (key: BenchmarkKey) => void }) {
  return (
    <Box role="group" aria-label="Benchmark" sx={{ display: 'flex', gap: '4px', p: '4px', bgcolor: BENCH.switcherBg, borderRadius: '8px' }}>
      {SHOWN_BENCHMARK_KEYS.map((key) => {
        const pressed = key === value;
        return (
          <Box
            key={key}
            component="button"
            type="button"
            aria-pressed={pressed}
            onClick={() => onChange(key)}
            sx={{
              fontFamily: BENCH.font,
              fontSize: '12px',
              lineHeight: '24px',
              fontWeight: pressed ? 700 : 600,
              px: '12px',
              border: `1px solid ${pressed ? BENCH.divider : 'transparent'}`,
              borderRadius: '4px',
              bgcolor: pressed ? BENCH.cardBg : 'transparent',
              color: pressed ? BENCH.ink : BENCH.muted,
              cursor: 'pointer',
            }}
          >
            {TAB_LABELS[key]}
          </Box>
        );
      })}
    </Box>
  );
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function subLine(group: MonitoringGroup): string {
  const students = plural(group.students, 'student', 'students');
  return group.teachers === undefined ? students : `${students} · ${plural(group.teachers, 'teacher', 'teachers')}`;
}

export interface GroupTableProps {
  title: 'Schools' | 'Teachers';
  data: BenchmarkMonitoring;
  groups: MonitoringGroup[];
  onSelect: (id: string) => void;
  emptyText: string;
}

export function GroupTable({ title, data, groups, onSelect, emptyText }: GroupTableProps) {
  const [key, setKey] = useState<BenchmarkKey>('writing');
  const mode = windowMode(data);
  const sorted = sortLeastCompleteFirst(groups, key);
  return (
    <Box component="section" data-testid={`${title.toLowerCase()}-table`} sx={CARD_SX}>
      <CardHeading title={title} right="Least complete first">
        <BenchmarkSwitcher value={key} onChange={setKey} />
      </CardHeading>
      <StatusHeaderRow first={title === 'Schools' ? 'School' : 'Teacher'} mode={mode} chevron />
      {sorted.length === 0 && (
        <Typography component="p" sx={{ fontFamily: BENCH.font, fontSize: '16px', lineHeight: '24px', color: BENCH.muted, py: '16px', m: 0 }}>
          {emptyText}
        </Typography>
      )}
      {sorted.map((group) => {
        const row = group.benchmarks.find((b) => b.key === key);
        const counts = row?.counts ?? { scored: 0, toScore: 0, inProgress: 0, notStarted: 0, base: 0 };
        const auto = row?.scoredBy === 'auto';
        return (
          <Box
            key={group.ref.id}
            component="button"
            type="button"
            data-testid="group-row"
            onClick={() => onSelect(group.ref.id)}
            sx={{
              ...ROW_SX,
              width: '100%',
              textAlign: 'left',
              background: 'none',
              border: 0,
              borderBottom: `1px solid ${BENCH.divider}`,
              px: 0,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(8, 8, 27, 0.04)' },
            }}
          >
            <NameCell name={group.ref.name} sub={subLine(group)} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ProgressBar counts={counts} mode={mode} />
            </Box>
            <CountCell value={counts.scored} width={COUNT_WIDTHS[0]} />
            <CountCell value={counts.toScore} dash={auto} width={COUNT_WIDTHS[1]} />
            <CountCell value={counts.inProgress} width={COUNT_WIDTHS[2]} note={closedNote(counts, 'inProgress', mode.open)} />
            <CountCell value={counts.notStarted} hot={hotNotStarted(counts)} width={COUNT_WIDTHS[3]} note={closedNote(counts, 'notStarted', mode.open)} />
            <ChevronRightIcon aria-hidden="true" sx={{ width: CHEVRON_WIDTH, flexShrink: 0, color: BENCH.muted, fontSize: 16 }} />
          </Box>
        );
      })}
    </Box>
  );
}
